import {
  Button,
  Checkbox,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Paper
} from '@material-ui/core/';
import green from '@material-ui/core/colors/green';
import orange from '@material-ui/core/colors/orange';
import red from '@material-ui/core/colors/red';
import withStyles from '@material-ui/core/styles/withStyles';
import { sortBy, camelCase, startCase } from 'lodash';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { SelectInput, useNotify } from 'react-admin';

import DocumentApi from '../../../../core/apis/DocumentApi';
import dataProvider from '../../../../core/providers/IccUserDataProvider';
import AutocompleteInput from '../../../../core/components/forked/input/AutocompleteInput';
import GridPopover from '../../../../core/components/grid/GridPopover';
import { investorStatus } from '../../../investors/InvestorLookups';
import { getIcError } from '../../../../core/providers/utils';
import { useForm } from 'react-final-form';
import VirtualizedGrid from '../../../../core/components/VirtualizedGrid';
import { EllipsisInput } from '../../manageBatch/ManageDocumentPropertiesDialog';
import LightTooltip from '../../../../core/components/LightTooltip';
import useNotifyServerError from '../../../../core/hooks/useNotifyServerError';
import { PendingIndicatorIcon } from '../../../../core/themes/icons/PendingIndicator';
import StatusColorDisplay from '../../../../core/components/StatusColorDisplay';

const inputStyle = () => ({
  container: {
    marginBottom: 0
  },
  root: {
    margin: '0',
    '& > div': {
      border: '0px !important'
    }
  },
  inputRoot: {
    border: 0,
    marginTop: '0px !important',
    flexWrap: 'inherit',
    '&:before': {
      borderBottom: 'none !important'
    }
  },
  autocompleteSuggestions: {
    zIndex: 1310, // it has to be bigger than dialog zIndex=1300
    backgroundColor: '#ffffff'
  },
  suggestionsPaper: {
    maxHeight: '50vh',
    height: '33vh'
  },
  inputPlaceholder: {
    '&::placeholder': {
      opacity: 1,
      color: 'red'
    }
  },
  checkbox: {
    padding: '0'
  },
  checkboxCell: {
    width: '24px',
    backgroundColor: '#ffffff !important'
  },
  emptyGridHeaderCell: {
    backgroundColor: '#ffffff !important'
  }
});

const splitTableStyles = {
  root: {
    margin: '0',
    '& > div': {
      border: '0px !important'
    }
  },
  gridButton: {
    fontSize: 14,
    fontWeight: 400,
    color: '#2962FF',
    marginRight: '16px',
    paddingTop: '2px',
    '&:hover': {
      backgroundColor: 'transparent'
    }
  },
  gridInfoBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    margin: '0px 0px 2px 0px'
  },
  infoBarNotMatchedLabel: {
    fontSize: 12,
    fontWeight: 400,
    background: '#D50000',
    color: '#FFFFFF',
    borderRadius: '2px',
    marginRight: '16px',
    padding: '3px'
  },
  infoBarMatchedLabel: {
    fontSize: 12,
    fontWeight: 400,
    background: '#00A645',
    color: '#FFFFFF',
    borderRadius: '2px',
    marginRight: '16px',
    padding: '3px'
  },
  infoBarBatchIdLabel: {
    fontSize: 15,
    fontWeight: 400
  }
};

//#region Form Validators

const validateFund = (value, allValues, props, field) => {
  if (!value) {
    return 'Required';
  }
  return undefined;
};
const validateInvestor = value => {
  if (!value) {
    return 'Required';
  }
  return undefined;
};
const validateTitle = value => {
  if (!value) {
    return 'Required';
  }
  return undefined;
};

//#endregion

//#region Custom columns

const MatchingColumn = ({ record, source }) => {
  const statusValue = startCase(camelCase(record[source]));
  let colorObject = { color: orange[500] };
  if (record[source] === 'MISMATCH') {
    colorObject = { color: red[500] };
  } else if (
    record[source] === 'MATCHED' ||
    record[source] === 'MANUALLY_SELECTED'
  ) {
    colorObject = { color: green[500] };
  }
  return <span style={colorObject}>{statusValue}</span>;
};

const InvestorColumn = withStyles(inputStyle)(({ ...props }) => {
  const {
    investors,
    investorFunds,
    doc,
    handleInvestorChange,
    classes,
    source
  } = props;
  const allChoices = [...investors];
  const optionRenderer = choice => {
    return choice ? choice.name : 'Select Investor';
  };

  return (
    <AutocompleteInput
      label=""
      optionText={optionRenderer}
      source={source}
      choices={allChoices}
      validate={[validateInvestor]}
      autoComplete="off"
      classes={{
        container: classes.container,
        root: classes.root,
        inputRoot: classes.inputRoot,
        inputInput: classes.inputPlaceholder
      }}
      placeholder={'Select Investor'}
      variant="standard"
      margin="none"
      showArrowDown={true}
      onChange={newValue => handleInvestorChange(newValue, doc, investorFunds)}
      options={{
        suggestionsContainerProps: {
          className: classes.autocompleteSuggestions
        }
      }}
      id="autocomplete-input-investor"
      menuId="autocomplete-input-menu-investor"
      getItemId={itemIndex =>
        `autocomplete-input-menu-item-investor-${itemIndex}`
      }
    />
  );
});

const FundColumn = withStyles(inputStyle)(({ ...props }) => {
  const { source, doc, handleFundChange, investorFunds, classes } = props;
  const investorFund =
    investorFunds.find(obj => obj.investorId === doc.investorId) || {};
  const { funds = [] } = investorFund;
  const fundChoices = sortBy(funds, 'fundName');
  const allChoices = [...fundChoices];
  const optionRenderer = choice => {
    return choice ? choice.name : 'Select Fund';
  };

  const disabled = doc.investorId === '' || doc.investorId === 'Un-Matched';
  return (
    <AutocompleteInput
      label=""
      optionText={optionRenderer}
      source={source}
      choices={allChoices}
      validate={[validateFund]}
      onChange={newValue => handleFundChange(newValue, doc)}
      autoComplete="off"
      classes={{
        container: classes.container,
        inputRoot: classes.inputRoot,
        inputInput: classes.inputPlaceholder
      }}
      variant="standard"
      margin="none"
      placeholder={'Select Fund'}
      showArrowDown={true}
      options={{
        disabled: disabled,
        suggestionsContainerProps: {
          className: classes.autocompleteSuggestions
        }
      }}
      id="autocomplete-input-fund"
      menuId="autocomplete-input-menu-fund"
      getItemId={itemIndex => `autocomplete-input-menu-item-fund-${itemIndex}`}
    />
  );
});

//#endregion

//empty documents grid component
const EmptyTableBody = withStyles(inputStyle)(props => {
  const {
    splitStatus,
    handleSplit,
    resourceupload,
    splitStatusDescription
  } = props;

  switch (splitStatus) {
    case 'NONE':
      return (
        <TableRow>
          <TableCell colSpan={9} align="center">
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ minHeight: 'calc(100vh - 355px)' }}
            >
              <Grid item align="center" xs={12}>
                <CircularProgress />
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>
      );
    case 'NOT_STARTED':
      return (
        <TableRow>
          <TableCell colSpan={9} align="center">
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ minHeight: 'calc(100vh - 355px)' }}
            >
              {resourceupload.batchStatus !== 'A' ? (
                <Grid item align="center" xs={12}>
                  <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    onClick={handleSplit}
                  >
                    Start Split
                  </Button>
                </Grid>
              ) : (
                <></>
              )}
            </Grid>
          </TableCell>
        </TableRow>
      );
    case 'PROCESSING':
      return (
        <TableRow>
          <TableCell colSpan={9} align="center">
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ minHeight: 'calc(100vh - 355px)' }}
            >
              <Grid item align="center" xs={12}>
                <CircularProgress />
                <Typography variant="subtitle1" align="center">
                  Splitting documents in progress...
                </Typography>
                <Typography variant="subtitle1" align="center">
                  Please wait
                </Typography>
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>
      );
    case 'ERROR':
      return (
        <TableRow>
          <TableCell colSpan={9} align="center">
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ minHeight: '430px' }}
            >
              <Grid item xs={12}>
                {splitStatusDescription ? (
                  <Typography variant="h5" align="center">
                    Split Error: {splitStatusDescription}
                  </Typography>
                ) : (
                  <Typography variant="h5" color="error" align="center">
                    Split Error
                  </Typography>
                )}
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>
      );
    default:
      return (
        <TableRow>
          <TableCell colSpan={9} align="center">
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              style={{ minHeight: '360px' }}
            >
              <Grid item xs={12}>
                <Typography variant="h5" color="error" align="center">
                  Unknown Split Status
                </Typography>
              </Grid>
            </Grid>
          </TableCell>
        </TableRow>
      );
  }
});

//empty grid columns header row
const EmptyTableHead = withStyles(inputStyle)(({ ...props }) => {
  const { classes, columns } = props;

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="checkbox" className={classes.checkboxCell}>
          <Checkbox className={classes.checkbox} />
        </TableCell>
        {columns
          .filter(col => col.id !== 'Selected')
          .map((column, index) => (
            <TableCell key={index} className={classes.emptyGridHeaderCell}>
              {column.id}
            </TableCell>
          ))}
      </TableRow>
    </TableHead>
  );
});

const SplitDocumentTable = withStyles(splitTableStyles)(props => {
  const {
    classes,
    splittedDocuments,
    resource,
    resourceupload,
    handleSplit,
    splitStatus,
    selectedRows,
    fundInvestors,
    splitStatusDescription,
    splitMethod,
    calculateInvestorsWithoutContactsIds,
    ...rest
  } = props;

  const form = useForm(props);
  const notify = useNotify();
  const notifyServerError = useNotifyServerError();
  const { investorWithoutContactsIds } = form.getState().values;
  const [unreferencedInvestors, setUnreferencedInvestors] = useState([]);
  const [investorWithoutContacts, setInvestorWithoutContacts] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [investorFunds, setInvestorFunds] = useState([]);

  const unMatchedDocs = useMemo(() => {
    return splittedDocuments.filter(obj => obj.matchingStatus === 'MISMATCH');
  }, [splittedDocuments]);

  const matchedDocs = useMemo(() => {
    return splittedDocuments.filter(obj => obj.matchingStatus !== 'MISMATCH');
  }, [splittedDocuments]);

  const visibleDocuments = useMemo(() => {
    return splittedDocuments.filter(document => !document.isFilteredOut);
  }, [splittedDocuments]);

  /**
   * Get the list of splitted document by batchId
   */
  const getSplittedDocuments = useCallback(
    async resourceUploadId => {
      try {
        const { data } = await dataProvider(
          'GET_LIST',
          'resourceuploaddocuments',
          {
            pagination: { perPage: -1 },
            filter: { resourceUploadId: resourceUploadId }
          }
        );
        return data;
      } catch (error) {
        notifyServerError(error);
      }
    },
    [notifyServerError]
  );

  //effect used to fetch documents from service after completed split,
  //re-initialize form so it's pristine after split and to set investors without contacts
  useEffect(() => {
    if (splitStatus && splitStatus === 'COMPLETED') {
      getDocs();

      async function getDocs() {
        const docs = await getSplittedDocuments(
          resourceupload.resourceUploadId
        );
        const splittedDocuments = (Array.isArray(docs) && docs) || [];
        const { reSplitMethod } = form.getState().values;
        form.initialize({
          splittedDocuments: splittedDocuments,
          selectedFilter: 'SHOW_ALL',
          reSplitMethod: reSplitMethod,
          investorWithoutContactsIds: []
        });
        calculateInvestorsWithoutContactsIds(splittedDocuments);
      }
    }
  }, [
    splitStatus,
    resourceupload,
    form,
    getSplittedDocuments,
    calculateInvestorsWithoutContactsIds
  ]);

  //effect used to set fund and investor lookups, investorWithoutContacts and unreferencedInvestors
  useEffect(() => {
    if (
      fundInvestors &&
      Array.isArray(fundInvestors) &&
      fundInvestors.length > 0
    ) {
      let investorFunds;
      const unsortedInvestors = [];
      let unreferencedInvestors = [];
      let investorWithoutContacts = [];
      investorFunds = fundInvestors.reduce(function(acc, curr) {
        const investorFund = acc.find(
          obj => obj.investorId === curr.investorId
        );
        if (
          investorFund &&
          Array.isArray(investorFund.funds) &&
          !investorFund.funds.find(obj => obj.fundId === curr.fundId)
        ) {
          const index = acc.findIndex(
            obj => obj.investorId === curr.investorId
          );
          acc[index] = {
            investorId: curr.investorId,
            funds: investorFund.funds.concat({
              id: curr.fundId,
              name: curr.fundName
            })
          };
          return acc;
        } else if (!investorFund) {
          unsortedInvestors.push({
            id: curr.investorId,
            name: curr.investorName,
            status: curr.investorStatus,
            fundId: curr.fundId,
            fundName: curr.fundName,
            fundStatus: curr.fundStatus,
            relationshipStatus: curr.status
          });
          return acc.concat({
            investorId: curr.investorId,
            funds: [{ id: curr.fundId, name: curr.fundName }]
          });
        }
        return acc;
      }, []);
      const investors = sortBy(unsortedInvestors, 'name', 'asc');
      const referencedInvestor = [];
      matchedDocs.forEach(doc => {
        referencedInvestor.push(doc.investorId);
      });
      unreferencedInvestors = investors.filter(investor => {
        return !referencedInvestor.includes(investor.id);
      });
      investorWithoutContacts = investors.filter(investor => {
        return investorWithoutContactsIds.includes(investor.id);
      });
      setUnreferencedInvestors(unreferencedInvestors);
      setInvestorWithoutContacts(investorWithoutContacts);
      setInvestorFunds(investorFunds);
      setInvestors(investors);
    }
  }, [fundInvestors, investorWithoutContactsIds, matchedDocs]);

  /**
   * Manually match document
   */
  const handleMatchDocument = useCallback(
    async (doc, changedDoc) => {
      let modifiedDoc = {};
      try {
        modifiedDoc = await DocumentApi.assignDocuments(doc.resourceUploadId, [
          changedDoc
        ]);
      } catch (error) {
        const errorMessage = getIcError(error);
        if (
          errorMessage ===
          'The document batch contains the same investor more than once which is not supported functionality. You will need to remove the multiple instance to move to the next step in the document process.'
        ) {
          notify('ra.notification.server', {
            type: 'warning',
            undoable: false,
            autoHideDuration: 30000,
            messageArgs: {
              message: errorMessage,
              isNewAdminCustomWarning: true
            }
          });
        } else {
          notifyServerError(error);
        }
      }

      if (modifiedDoc && modifiedDoc[0]) {
        modifiedDoc[0].selected = false;
        modifiedDoc[0].isFilteredOut = false;
        modifiedDoc[0].numberOfPages = doc.numberOfPages;

        let changedSplittedDocuments = [...splittedDocuments];
        for (var i = changedSplittedDocuments.length - 1; i >= 0; i--) {
          if (changedSplittedDocuments[i].id === doc.id) {
            changedSplittedDocuments.splice(i, 1, modifiedDoc[0]);
            form.change('splittedDocuments', changedSplittedDocuments);
            break;
          }
        }
        calculateInvestorsWithoutContactsIds(changedSplittedDocuments);
      }
    },
    [
      notify,
      form,
      splittedDocuments,
      calculateInvestorsWithoutContactsIds,
      notifyServerError
    ]
  );

  //handle manually change of fund/investor
  const handleMatchChanged = useCallback(
    async (newValue, doc, investorFunds) => {
      if (!newValue) {
        return;
      }
      if (investorFunds) {
        if (doc.fundId === 'MultiFund' || newValue === 'Un-Matched') {
          return;
        }
        // If the already selected fund not associated with the investor then do nothing
        const investorFund =
          investorFunds.find(obj => obj.investorId === newValue) || {};
        const { funds = [] } = investorFund;
        if (!funds.find(obj => obj.id === doc.fundId)) {
          return;
        }
      } else if (newValue === 'MultiFund' || doc.investorId === 'Un-Matched') {
        return;
      }

      const changedDocument = {
        id: doc.id,
        fundId: investorFunds ? doc.fundId : newValue,
        investorId: investorFunds ? newValue : doc.investorId,
        documentTitle: doc.documentTitle,
        documentDate: doc.documentDate,
        documentSecurityId: doc.documentSecurityId,
        dueDate: doc.dueDate,
        watermarkId: doc.watermarkId === 0 ? null : doc.watermarkId,
        categoryId: doc.categoryId,
        subcategoryId: doc.subcategoryId
      };

      await handleMatchDocument(doc, changedDocument);
    },
    [handleMatchDocument]
  );

  const filterByDocStatus = useCallback((item, selectedFilter) => {
    switch (selectedFilter) {
      case 'SHOW_ALL':
        return true;
      case 'NOT_MATCHED':
        return item.matchingStatus === 'MISMATCH';
      case 'MANUALLY_MATCHED':
        return item.matchingStatus === 'MANUALLY_SELECTED';
      case 'MATCHED_BY_SYSTEM':
        return (
          item.matchingStatus !== 'MANUALLY_SELECTED' &&
          item.matchingStatus !== 'MISMATCH'
        );
      case 'MATCHED':
        return item.matchingStatus !== 'MISMATCH';
      default:
        return false;
    }
  }, []);

  const handleFilterChange = useCallback(
    newFilter => {
      let changedSplittedDocuments = [...splittedDocuments];
      changedSplittedDocuments.forEach(document => {
        document.isFilteredOut = !filterByDocStatus(document, newFilter);
      });
      form.change('splittedDocuments', changedSplittedDocuments);
    },
    [form, splittedDocuments, filterByDocStatus]
  );

  /**
   *  Handle select all checbox clicked
   */
  const handleSelectAllClick = useCallback(
    event => {
      splittedDocuments.forEach((document, index) => {
        if (!document.isFilteredOut) {
          form.change(
            `splittedDocuments[${index}].selected`,
            event.target.checked
          );
        }
      });
    },
    [form, splittedDocuments]
  );

  /**
   *  Handle row checkbox clicked
   */
  const handleClick = useCallback(
    id => {
      const index = splittedDocuments.findIndex(document => document.id === id);
      if (index >= 0) {
        form.change(
          `splittedDocuments[${index}].selected`,
          !splittedDocuments[index].selected
        );
      }
    },
    [form, splittedDocuments]
  );

  /**
   * Downlaod existing batch and save file locally
   * @param {*} document the document object
   */
  const downloadDocument = useCallback(
    async (documentId, fileName) => {
      try {
        await DocumentApi.downloadDocumentAsFile(documentId, fileName);
      } catch (error) {
        notifyServerError(error);
      }
    },
    [notifyServerError]
  );

  //set order column value if sorting is clicked in underlying grid
  const setDocuments = useCallback(
    newDocuments => {
      let changedSplittedDocuments = [...splittedDocuments];
      if (changedSplittedDocuments.length > 0) {
        changedSplittedDocuments.forEach((document, index) => {
          document.order = newDocuments.findIndex(e => e.id === document.id);
        });
      }
      form.change('splittedDocuments', changedSplittedDocuments);
    },
    [form, splittedDocuments]
  );

  const allColumns = [
    {
      id: 'Selected',
      source: 'isItemSelected',
      type: 'checkbox',
      sortable: false,
      width: 30,
      onClick: handleSelectAllClick,
      renderer: params => {
        const { row_data, rowVal } = params;
        const cellStyle = {
          ...row_data.style,
          color: '#000000',
          borderTop: row_data.rowIndex === 1 ? 'none' : '1px solid #E0E0E0',
          fontSize: '14px',
          background: 'white',
          padding: 0
        };
        const isItemSelected = selectedRows.indexOf(rowVal.id) !== -1;
        return (
          <div style={cellStyle} key={rowVal.id}>
            <Checkbox
              checked={isItemSelected}
              onClick={() => handleClick(rowVal.id)}
              className={classes.gridCheckbox}
            />
          </div>
        );
      }
    },
    {
      id: 'File Name',
      source: 'originalFileName',
      sortable: true,
      type: 'link',
      renderer: params => {
        const { row_data, rowVal } = params;
        const cellStyle = {
          ...row_data.style,
          color: '#000000',
          borderTop: row_data.rowIndex === 1 ? 'none' : '1px solid #E0E0E0',
          fontSize: '14px',
          background: 'white',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        };
        return (
          <div style={cellStyle} key={rowVal.originalFileName}>
            <Button
              onClick={() =>
                downloadDocument(rowVal.id, rowVal.originalFileName)
              }
              className={classes.gridButton}
            >
              {rowVal.originalFileName}
            </Button>
          </div>
        );
      }
    },
    {
      id: 'File Size (KB)',
      source: 'fileSize',
      sortable: true,
      type: 'number',
      renderer: params => {
        const { column, row_data, rowVal } = params;
        const cellStyle = {
          ...row_data.style,
          color: '#000000',
          fontSize: '14px',
          fontWeight: '400',
          textAlign: 'left',
          background: 'white',
          padding: '5px 8px 6px 8px',
          borderTop: row_data.rowIndex === 1 ? 'none' : '1px solid #E0E0E0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        };
        return (
          <span
            style={cellStyle}
            key={`${rowVal[column.source]}:${rowVal.originalFileName}`}
          >
            {rowVal[column.source]}
          </span>
        );
      }
    },
    {
      id: 'Investors',
      source: 'investorId',
      sortable: true,
      type: 'lookup',
      lookupTextField: 'name',
      lookupIdField: 'id',
      options: investors,
      renderer: params => {
        const { column, row_data, rowVal } = params;
        const cellStyle = {
          ...row_data.style,
          color: '#000000',
          fontSize: '14px',
          fontWeight: '400',
          textAlign: 'left',
          background: 'white',
          borderTop: row_data.rowIndex === 1 ? 'none' : '1px solid #E0E0E0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        };
        const index = splittedDocuments.findIndex(e => e && e.id === rowVal.id);
        return (
          <span style={cellStyle} key={index}>
            <InvestorColumn
              source={`splittedDocuments[${index}].${column.source}`}
              investors={investors}
              investorFunds={investorFunds}
              handleInvestorChange={handleMatchChanged}
              doc={rowVal}
            />
          </span>
        );
      }
    },
    {
      id: 'Funds',
      source: 'fundId',
      sortable: true,
      type: 'lookup',
      lookupTextField: 'fundName',
      lookupIdField: 'fundId',
      options: fundInvestors,
      renderer: params => {
        const { column, row_data, rowVal } = params;
        const cellStyle = {
          ...row_data.style,
          color: '#000000',
          fontSize: '14px',
          fontWeight: '400',
          textAlign: 'left',
          background: 'white',
          borderTop: row_data.rowIndex === 1 ? 'none' : '1px solid #E0E0E0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        };
        const index = splittedDocuments.findIndex(e => e && e.id === rowVal.id);
        return (
          <span style={cellStyle} key={`${index}:${rowVal.originalFileName}`}>
            <FundColumn
              doc={rowVal}
              source={`splittedDocuments[${index}].${column.source}`}
              investorFunds={investorFunds}
              handleFundChange={handleMatchChanged}
              perPage={-1}
            />
          </span>
        );
      }
    },
    {
      id: 'Document Title',
      source: 'documentTitle',
      sortable: true,
      type: 'input',
      renderer: params => {
        const { column, row_data, rowVal } = params;
        const cellStyle = {
          ...row_data.style,
          color: '#000000',
          borderTop: row_data.rowIndex === 1 ? 'none' : '1px solid #E0E0E0',
          fontSize: '14px',
          fontWeight: '400'
          //borderLeft: '1px solid #E0E0E0'
        };
        const index = splittedDocuments.findIndex(e => e && e.id === rowVal.id);
        return (
          <LightTooltip
            placement="bottom"
            title={rowVal[column.source]}
            key={`${rowVal.id}:${index}`}
          >
            <div style={cellStyle}>
              <EllipsisInput
                source={`splittedDocuments[${index}].${column.source}`}
                label=""
                className={classes.root}
                variant="standard"
                helperText={false}
                validate={validateTitle}
              />
            </div>
          </LightTooltip>
        );
      }
    },
    {
      id: '# of Pages',
      source: 'numberOfPages',
      sortable: true,
      type: 'number',
      renderer: params => {
        const { column, row_data, rowVal } = params;
        const cellStyle = {
          ...row_data.style,
          color: '#000000',
          fontSize: '14px',
          fontWeight: '400',
          textAlign: 'left',
          background: 'white',
          padding: '5px 8px 6px 8px',
          borderTop: row_data.rowIndex === 1 ? 'none' : '1px solid #E0E0E0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        };
        return (
          <span
            style={cellStyle}
            key={`${rowVal[column.source]}:${row_data.rowIndex}`}
          >
            {rowVal[column.source]}
          </span>
        );
      }
    },
    {
      id: 'Matching Data',
      source: 'matchingData',
      sortable: true,
      type: 'text',
      renderer: params => {
        const { column, row_data, rowVal } = params;
        const cellStyle = {
          ...row_data.style,
          color: '#000000',
          fontSize: '14px',
          fontWeight: '400',
          textAlign: 'left',
          background: 'white',
          padding: '5px 8px 6px 8px',
          borderTop: row_data.rowIndex === 1 ? 'none' : '1px solid #E0E0E0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        };
        return (
          <span style={cellStyle} key={rowVal[column.source]}>
            {rowVal[column.source]}
          </span>
        );
      }
    },
    {
      id: 'Matching Status',
      source: 'matchingStatus',
      sortable: true,
      type: 'text',
      renderer: params => {
        const { column, row_data, rowVal } = params;
        const cellStyle = {
          ...row_data.style,
          color: '#000000',
          fontSize: '14px',
          fontWeight: '400',
          textAlign: 'left',
          background: 'white',
          padding: '5px 8px 6px 8px',
          borderTop: row_data.rowIndex === 1 ? 'none' : '1px solid #E0E0E0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        };
        return (
          <span
            style={cellStyle}
            key={`${rowVal[column.source]}:${row_data.rowIndex}`}
          >
            <MatchingColumn record={rowVal} source={column.source} />
          </span>
        );
      }
    }
  ];

  //add one row for columns with column names to grid data
  const columnRow = allColumns.reduce(
    (acc, cur) => ({ ...acc, [cur.source]: cur.id }),
    {}
  );
  let gridData = visibleDocuments.slice();
  gridData = sortBy(gridData, 'order', 'asc');
  gridData.unshift(columnRow);

  return (
    <Grid container justifyContent="center" alignItems="center">
      <Grid item xs={12}>
        {splitStatus === 'COMPLETED' ? (
          <div>
            <Grid item>
              <div className={classes.gridInfoBar}>
                <div>
                  <span
                    className={classes.infoBarNotMatchedLabel}
                    id="not-matched-documents"
                  >
                    Not Matched: {unMatchedDocs.length}
                  </span>
                  <span
                    className={classes.infoBarMatchedLabel}
                    id="matched-documents"
                  >
                    Matched: {matchedDocs.length}
                  </span>
                  <GridPopover
                    text={`Investor without contacts: ${investorWithoutContacts.length}`}
                    classes={{ button: classes.button }}
                    popoverContent={
                      <Paper>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>ID</TableCell>
                              <TableCell>Investors</TableCell>
                              <TableCell>Status</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {investorWithoutContacts.map((investor, index) => {
                              const invStatus = investorStatus.find(
                                inv => inv.id === investor.status
                              );
                              return (
                                <TableRow key={index}>
                                  <TableCell>{investor.id}</TableCell>
                                  <TableCell>{investor.name}</TableCell>
                                  <TableCell>
                                    {invStatus ? invStatus.name : ''}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Paper>
                    }
                  />
                  <GridPopover
                    text={`Unreferenced Investors: ${unreferencedInvestors.length}`}
                    classes={{ button: classes.button }}
                    popoverContent={
                      <Paper>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Investors</TableCell>
                              <TableCell>Investor ID</TableCell>
                              <TableCell>Funds</TableCell>
                              <TableCell>Fund ID</TableCell>
                              <TableCell>Relationship</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {unreferencedInvestors.map((investor, index) => {
                              return (
                                <TableRow key={index}>
                                  <TableCell>
                                    <div
                                      style={{
                                        display: 'flex',
                                        paddingLeft:
                                          investor.status !== 'INACTIVE' &&
                                          '16px'
                                      }}
                                    >
                                      {investor.status === 'INACTIVE' && (
                                        <PendingIndicatorIcon title="Inactive Investor" />
                                      )}
                                      {investor.name}
                                    </div>
                                  </TableCell>
                                  <TableCell>{investor.id}</TableCell>
                                  <TableCell>
                                    <div
                                      style={{
                                        display: 'flex',
                                        paddingLeft:
                                          investor.fundStatus !== 'PENDING' &&
                                          '16px'
                                      }}
                                    >
                                      {investor.fundStatus === 'PENDING' && (
                                        <PendingIndicatorIcon title="Pending Fund" />
                                      )}
                                      {investor.fundName}
                                    </div>
                                  </TableCell>
                                  <TableCell>{investor.fundId}</TableCell>
                                  <TableCell>
                                    <StatusColorDisplay
                                      status={investor.relationshipStatus}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </Paper>
                    }
                  />
                  <span className={classes.infoBarBatchIdLabel}>
                    Batch ID: {resourceupload.resourceUploadId}
                  </span>
                </div>
                <div>
                  <SelectInput
                    label=""
                    variant="standard"
                    source={`selectedFilter`}
                    choices={[
                      { id: 'SHOW_ALL', name: 'Show All' },
                      { id: 'NOT_MATCHED', name: 'Not Matched' },
                      { id: 'MATCHED_BY_SYSTEM', name: 'Matched By System' },
                      { id: 'MANUALLY_MATCHED', name: 'Manually Matched' },
                      { id: 'MATCHED', name: 'All Matched' }
                    ]}
                    onChange={event => {
                      handleFilterChange(event.target.value);
                    }}
                    helperText={false}
                  />
                </div>
              </div>
            </Grid>
          </div>
        ) : (
          <></>
        )}
        <Paper>
          {splitStatus === 'COMPLETED' ? (
            <VirtualizedGrid
              columns={allColumns}
              data={gridData}
              fixedColumns={0}
              height={`calc(100vh - 350px)`}
              minHeight="400px"
              rowHeight={30}
              setDocuments={setDocuments}
            />
          ) : (
            <Table>
              <EmptyTableHead columns={allColumns} />
              <TableBody>
                <EmptyTableBody
                  handleSplit={handleSplit}
                  splitStatus={splitStatus}
                  resourceupload={resourceupload}
                  splitStatusDescription={splitStatusDescription}
                  {...rest}
                />
              </TableBody>
            </Table>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
});

export default SplitDocumentTable;
